import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { fetchContracts } from '../../api/contract';
import { fetchCompanyInventory, fetchCompanyInventoryMasters } from '../../api/companyInventory';
import { fetchPartVendors } from '../../api/partVendor';
import {
  approvePurchaseOrder,
  createPurchaseOrder,
  deletePurchaseOrder,
  deletePurchaseOrderAttachment,
  downloadPurchaseOrderAttachment,
  downloadPurchaseOrderPdf,
  fetchPurchaseOrder,
  fetchPurchaseOrders,
  markPurchaseOrderOrdered,
  cancelPurchaseOrderOrdered,
  revokePurchaseOrder,
  updatePurchaseOrder,
  uploadPurchaseOrderAttachment,
} from '../../api/purchaseOrder';
import { useCompanyStore } from '../../store/companyStore';
import {notifyError, reportError} from '../../utils/notify';
import { Contract } from '../../types/contract';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
  inventoryKindLabel,
} from '../../types/companyInventory';
import { PartVendor } from '../../types/partVendor';
import {
  calcOrderLineAmount,
  formatOrderQty,
  formatPurchaseOrderFileSize,
  formatUnitPriceDisplay,
  formatUnitPriceInput,
  formatWon,
  parseUnitPriceInput,
  PurchaseOrder,
  PurchaseOrderAttachment,
  PurchaseOrderAttachmentKind,
  PurchaseOrderItemPayload,
  PurchaseOrderPayload,
  PurchaseOrderStatus,
  purchaseOrderAttachmentKindLabel,
  purchaseOrderHasApprover,
  purchaseOrderStatusLabel,
} from '../../types/purchaseOrder';
import { APPROVAL_TARGET_PURCHASE_ORDER } from '../../types/approval';
import { canConfirmInspection } from '../../types/companyRole';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import ApprovalStampBox from '../approval/ApprovalStampBox';

import CompanyInventoryPickerModal from './CompanyInventoryPickerModal';
import PartVendorPickerModal from './PartVendorPickerModal';
import '../contract/ContractPanel.css';
import './InspectionFormSheet.css';
import './NonconformanceReportPanel.css';
import './PurchaseOrderPanel.css';

type ViewMode = 'list' | 'create' | 'edit' | 'detail';
type StatusFilter = 'ALL' | PurchaseOrderStatus;

type LineForm = {
  key: string;
  masterId: number | null;
  inventoryId: number | null;
  itemName: string;
  specText: string;
  unit: string;
  orderQtyText: string;
  unitPriceText: string;
  remark: string;
};

type PendingFile = {
  file: File;
  kind: PurchaseOrderAttachmentKind;
};

interface Props {
  /** 계약 상세/임베드에서 해당 계약 발주만 표시 */
  contractId?: number | null;
  /** true면 상단 타이틀·메뉴 여백을 줄임 */
  embedded?: boolean;
  /** 상세 열림 (계약 요약에서 이동용) */
  initialPurchaseOrderId?: number | null;
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'DRAFT', label: '작성완료' },
  { value: 'APPROVED', label: '승인완료' },
  { value: 'ORDERED', label: '발주완료' },
  { value: 'CLOSED', label: '마감' },
  { value: 'CANCELLED', label: '취소' },
];

function newLine(): LineForm {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    masterId: null,
    inventoryId: null,
    itemName: '',
    specText: '',
    unit: '',
    orderQtyText: '1',
    unitPriceText: '',
    remark: '',
  };
}

function textOrDash(value?: string | null) {
  const v = value?.trim();
  return v ? v : '-';
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function PurchaseOrderPanel({
  contractId = null,
  embedded = false,
  initialPurchaseOrderId = null,
}: Props) {
  const companyId = useCompanyStore((state) => state.companyId);
  const role = useCompanyStore((state) => state.role);
  const canApproveDirect = canConfirmInspection(role);
  const [rows, setRows] = useState<PurchaseOrder[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [masters, setMasters] = useState<CompanyInventoryMaster[]>([]);
  const [vendors, setVendors] = useState<PartVendor[]>([]);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PurchaseOrder | null>(null);
  const [saving, setSaving] = useState(false);

  const [formContractId, setFormContractId] = useState<number | null>(contractId);
  const [orderNo, setOrderNo] = useState('');
  const [orderNoHint, setOrderNoHint] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [deliveryDueDate, setDeliveryDueDate] = useState('');
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [vendorContactName, setVendorContactName] = useState('');
  const [vendorPhone, setVendorPhone] = useState('');
  const [vendorFax, setVendorFax] = useState('');
  const [status, setStatus] = useState<PurchaseOrderStatus>('DRAFT');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<LineForm[]>([newLine()]);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [pickerLineKey, setPickerLineKey] = useState<string | null>(null);
  const [savedAttachments, setSavedAttachments] = useState<PurchaseOrderAttachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadKind, setUploadKind] = useState<PurchaseOrderAttachmentKind>('DRAWING');
  const [uploading, setUploading] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(
        await fetchPurchaseOrders({
          contractId: contractId ?? undefined,
          keyword,
        })
      );
      setError('');
    } catch {
      reportError(setError, '발주서 목록을 불러오지 못했습니다. 서버를 재시작했는지 확인해 주세요.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [contractId, keyword]);

  const loadMeta = useCallback(async () => {
    try {
      const [contractList, invList, masterList, vendorList] = await Promise.all([
        fetchContracts(),
        fetchCompanyInventory(),
        fetchCompanyInventoryMasters(),
        fetchPartVendors(),
      ]);
      setContracts(contractList);
      setInventory(invList);
      setMasters(masterList);
      setVendors(vendorList);
    } catch {
      // 폼에서 개별 처리
    }
  }, []);

  useEffect(() => {
    setViewMode('list');
    const timer = window.setTimeout(() => void load(), 200);
    return () => window.clearTimeout(timer);
  }, [companyId, load, setViewMode]);

  useEffect(() => {
    void loadMeta();
  }, [companyId, loadMeta]);

  useEffect(() => {
    if (contractId != null) {
      setFormContractId(contractId);
    }
  }, [contractId]);

  useEffect(() => {
    if (initialPurchaseOrderId == null) return;
    void (async () => {
      try {
        const next = await fetchPurchaseOrder(initialPurchaseOrderId);
        setDetail(next);
        setEditingId(next.purchaseOrderId);
        setSavedAttachments(next.attachments ?? []);
        setViewMode('detail');
      } catch {
        reportError(setError, '발주서를 불러오지 못했습니다.');
      }
    })();
  }, [initialPurchaseOrderId]);

  const statusCounts = useMemo(() => {
    const map: Record<string, number> = {
      ALL: rows.length,
      DRAFT: 0,
      ORDERED: 0,
      APPROVED: 0,
      CLOSED: 0,
      CANCELLED: 0,
    };
    rows.forEach((row) => {
      const key = row.status || 'DRAFT';
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'ALL') return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const formTotalAmount = useMemo(() => {
    return lines.reduce((sum, line) => {
      const amount = calcOrderLineAmount(line.orderQtyText, line.unitPriceText);
      return amount == null ? sum : sum + amount;
    }, 0);
  }, [lines]);

  const formHasPricedLine = useMemo(
    () => lines.some((line) => calcOrderLineAmount(line.orderQtyText, line.unitPriceText) != null),
    [lines]
  );

  const resetForm = () => {
    setFormContractId(contractId);
    setOrderNo('');
    setOrderNoHint('');
    setOrderDate(new Date().toISOString().slice(0, 10));
    setDeliveryDueDate('');
    setVendorId(null);
    setVendorName('');
    setVendorContactName('');
    setVendorPhone('');
    setVendorFax('');
    setStatus('DRAFT');
    setMemo('');
    setLines([newLine()]);
    setVendorPickerOpen(false);
    setPickerLineKey(null);
    setSavedAttachments([]);
    setPendingFiles([]);
    setUploadKind('DRAWING');
  };

  const backToList = () => {
    setViewMode('list');
    setEditingId(null);
    setDetail(null);
    resetForm();
  };

  const fillFromOrder = (order: PurchaseOrder) => {
    setFormContractId(order.contractId ?? null);
    setOrderNo(order.orderNo || '');
    setOrderNoHint('');
    setOrderDate(order.orderDate?.slice(0, 10) || '');
    setDeliveryDueDate(order.deliveryDueDate?.slice(0, 10) || '');
    setVendorId(order.vendorId ?? null);
    setVendorName(order.vendorName || '');
    setVendorContactName(order.vendorContactName || '');
    setVendorPhone(order.vendorPhone || '');
    setVendorFax(order.vendorFax || '');
    setStatus((order.status as PurchaseOrderStatus) || 'DRAFT');
    setMemo(order.memo || '');
    const items = order.items ?? [];
    setLines(
      items.length > 0
        ? items.map((item) => {
            const matched = inventory.find((row) => row.inventoryId === item.inventoryId);
            return {
              key: `i-${item.itemId ?? item.inventoryId}-${Math.random().toString(36).slice(2, 6)}`,
              masterId: matched?.masterId ?? null,
              inventoryId: item.inventoryId,
              itemName: item.itemName || matched?.itemName || '',
              specText: item.specText || '',
              unit: item.unit || '',
              orderQtyText: formatOrderQty(item.orderQty) === '-' ? '1' : formatOrderQty(item.orderQty),
              unitPriceText: formatUnitPriceDisplay(item.unitPrice),
              remark: item.remark || '',
            };
          })
        : [newLine()]
    );
    setSavedAttachments(order.attachments ?? []);
    setPendingFiles([]);
  };

  const openCreate = () => {
    setEditingId(null);
    setDetail(null);
    resetForm();
    setError('');
    setOrderNo('');
    setOrderNoHint('저장 시 발주번호가 자동 발급됩니다.');
    setViewMode('create');
  };

  const openDetail = async (row: PurchaseOrder) => {
    setEditingId(row.purchaseOrderId);
    setDetail(row);
    setSavedAttachments(row.attachments ?? []);
    setError('');
    setViewMode('detail');
    try {
      const next = await fetchPurchaseOrder(row.purchaseOrderId);
      setDetail(next);
      setSavedAttachments(next.attachments ?? []);
    } catch {
      // keep list row
    }
  };

  const openEdit = async (row: PurchaseOrder) => {
    setEditingId(row.purchaseOrderId);
    setError('');
    try {
      const next = await fetchPurchaseOrder(row.purchaseOrderId);
      setDetail(next);
      fillFromOrder(next);
      setViewMode('edit');
    } catch {
      reportError(setError, '발주서를 불러오지 못했습니다.');
    }
  };

  const applyVendor = (vendor: PartVendor) => {
    setVendorId(vendor.vendorId);
    setVendorName(vendor.vendorName || '');
    setVendorContactName(vendor.contactName || '');
    setVendorPhone(vendor.mobile?.trim() || vendor.phone?.trim() || '');
    setVendorFax(vendor.fax?.trim() || '');
  };

  const clearVendor = () => {
    setVendorId(null);
    setVendorName('');
    setVendorContactName('');
    setVendorPhone('');
    setVendorFax('');
  };

  const applyInventoryToLine = (key: string, item: CompanyInventoryItem) => {
    setLines((prev) =>
      prev.map((line) =>
        line.key !== key
          ? line
          : {
              ...line,
              masterId: item.masterId ?? null,
              inventoryId: item.inventoryId,
              itemName: item.itemName,
              specText: item.specText || '',
              unit: item.unit || '',
            }
      )
    );
  };

  const patchLine = (key: string, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const lineItemSummary = (line: LineForm) => {
    if (line.inventoryId == null) return null;
    const matched = inventory.find((row) => row.inventoryId === line.inventoryId);
    const kind = matched ? inventoryKindLabel(matched.itemKind) : null;
    const name = line.itemName || matched?.itemName || '품목';
    const spec = line.specText || matched?.specText || '';
    return kind ? `[${kind}] ${name}${spec ? ` / ${spec}` : ''}` : `${name}${spec ? ` / ${spec}` : ''}`;
  };

  const buildPayload = (): PurchaseOrderPayload | null => {
    const items: PurchaseOrderItemPayload[] = [];
    for (const line of lines) {
      if (line.inventoryId == null) {
        notifyError('품목을 모두 선택해 주세요.');
        return null;
      }
      const qty = parseUnitPriceInput(line.orderQtyText);
      if (qty == null || qty <= 0) {
        notifyError('발주 수량이 올바르지 않습니다.');
        return null;
      }
      let unitPrice: number | null = null;
      if (line.unitPriceText.trim()) {
        unitPrice = parseUnitPriceInput(line.unitPriceText);
        if (unitPrice == null || unitPrice < 0) {
          notifyError('단가가 올바르지 않습니다.');
          return null;
        }
      }
      items.push({
        inventoryId: line.inventoryId,
        itemName: line.itemName || undefined,
        specText: line.specText || undefined,
        unit: line.unit || undefined,
        orderQty: qty,
        unitPrice,
        remark: line.remark.trim() || undefined,
      });
    }
    if (items.length === 0) {
      notifyError('발주 품목을 한 줄 이상 추가해 주세요.');
      return null;
    }
    return {
      contractId: formContractId,
      orderNo: viewMode === 'create' ? undefined : orderNo.trim() || undefined,
      orderDate: orderDate || null,
      deliveryDueDate: deliveryDueDate || null,
      vendorId,
      vendorName: vendorName.trim() || undefined,
      vendorContactName: vendorContactName.trim() || undefined,
      vendorPhone: vendorPhone.trim() || undefined,
      vendorFax: vendorFax.trim() || undefined,
      status: 'DRAFT',
      memo: memo.trim() || undefined,
      items,
    };
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    try {
      setSaving(true);
      setError('');
      let id = editingId;
      if (editingId) {
        await updatePurchaseOrder(editingId, payload);
      } else {
        const created = await createPurchaseOrder(payload);
        id = created.purchaseOrderId;
        window.alert(`발주번호 ${created.orderNo || '-'}로 등록되었습니다.`);
        for (const item of pendingFiles) {
          await uploadPurchaseOrderAttachment(created.purchaseOrderId, item.file, item.kind);
        }
      }
      await load();
      if (id) {
        const next = await fetchPurchaseOrder(id);
        setDetail(next);
        setEditingId(next.purchaseOrderId);
        setSavedAttachments(next.attachments ?? []);
        setViewMode('detail');
        resetForm();
        return;
      }
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '발주서 저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: PurchaseOrder) => {
    if (!window.confirm(`발주서 ${row.orderNo || `#${row.purchaseOrderId}`}을(를) 삭제할까요?`)) {
      return;
    }
    try {
      await deletePurchaseOrder(row.purchaseOrderId);
      if (editingId === row.purchaseOrderId) backToList();
      await load();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '발주서 삭제에 실패했습니다.'
      );
    }
  };

  const handleApproveDirect = async () => {
    if (!detail) return;
    const alreadyOrdered = detail.status === 'ORDERED';
    if (
      !window.confirm(
        alreadyOrdered
          ? '발주완료 건을 승인할까요?\n승인란이 채워지고 발주완료 상태는 유지됩니다.'
          : '발주서를 바로 승인할까요?\n승인 후에는 수정·삭제가 잠깁니다. 이후 발주완료 처리도 가능합니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const updated = await approvePurchaseOrder(detail.purchaseOrderId);
      setDetail(updated);
      setSavedAttachments(updated.attachments ?? []);
      setApprovalMessage(
        alreadyOrdered
          ? '발주완료 건이 승인되었습니다. (상태는 발주완료 유지)'
          : '발주서가 승인되었습니다.'
      );
      await load();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (!detail || !detail.canRevoke) return;
    const keepOrdered = detail.status === 'ORDERED';
    if (
      !window.confirm(
        keepOrdered
          ? '승인을 철회할까요?\n승인란·검토란이 지워지고 발주완료 상태는 유지됩니다.'
          : '승인을 철회할까요?\n승인란·검토란이 지워지고 작성완료 상태로 돌아갑니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const updated = await revokePurchaseOrder(detail.purchaseOrderId);
      setDetail(updated);
      setSavedAttachments(updated.attachments ?? []);
      setApprovalMessage(
        keepOrdered
          ? '승인이 철회되었습니다. 발주완료 상태는 유지됩니다.'
          : '승인이 철회되었습니다.'
      );
      await load();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '승인 철회에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleMarkOrdered = async () => {
    if (!detail) return;
    if (
      !window.confirm(
        '발주 완료로 변경할까요?\n발주완료 후에는 수정·삭제가 잠기고 PDF를 받을 수 있습니다.\n승인 전이어도 발주·PDF가 가능하며, 나중에 승인할 수 있습니다.\n발주완료한 본인만 발주 취소할 수 있습니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const updated = await markPurchaseOrderOrdered(detail.purchaseOrderId);
      setDetail(updated);
      setSavedAttachments(updated.attachments ?? []);
      setApprovalMessage('발주 완료 처리되었습니다.');
      await load();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '발주 완료 처리에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleCancelOrdered = async () => {
    if (!detail || !detail.canCancelOrdered) return;
    if (
      !window.confirm(
        '발주완료를 취소할까요?\n이전 상태(작성완료 또는 승인완료)로 돌아가며, 승인 스탬프가 있으면 유지됩니다.'
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError('');
      const updated = await cancelPurchaseOrderOrdered(detail.purchaseOrderId);
      setDetail(updated);
      setSavedAttachments(updated.attachments ?? []);
      setApprovalMessage('발주완료가 취소되었습니다. 다시 수정할 수 있습니다.');
      await load();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '발주 취소에 실패했습니다.'
      );
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      setError('');
      const name = detail.orderNo?.trim() || String(detail.purchaseOrderId);
      await downloadPurchaseOrderPdf(detail.purchaseOrderId, `발주서_${name}.pdf`);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : 'PDF 다운로드에 실패했습니다.');
    } finally {
      setPdfDownloading(false);
    }
  };

  const handlePickFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const next: PendingFile[] = Array.from(fileList).map((file) => ({ file, kind: uploadKind }));
    setPendingFiles((prev) => [...prev, ...next]);
  };

  const handleUploadNow = async (fileList: FileList | null) => {
    if (!editingId || !fileList || fileList.length === 0) return;
    try {
      setUploading(true);
      setError('');
      let latest: PurchaseOrder | null = null;
      for (const file of Array.from(fileList)) {
        latest = await uploadPurchaseOrderAttachment(editingId, file, uploadKind);
      }
      if (latest) {
        setDetail(latest);
        setSavedAttachments(latest.attachments ?? []);
      }
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '첨부파일 업로드에 실패했습니다.'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment: PurchaseOrderAttachment) => {
    if (!editingId) return;
    try {
      const blob = await downloadPurchaseOrderAttachment(editingId, attachment.attachmentId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.fileName || 'attachment';
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch {
      notifyError('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const handleDeleteAttachment = async (attachment: PurchaseOrderAttachment) => {
    if (!editingId) return;
    if (!window.confirm(`「${attachment.fileName}」을(를) 삭제할까요?`)) return;
    try {
      await deletePurchaseOrderAttachment(editingId, attachment.attachmentId);
      const next = await fetchPurchaseOrder(editingId);
      setDetail(next);
      setSavedAttachments(next.attachments ?? []);
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    }
  };

  const renderAttachmentEditor = (immediateUpload: boolean, readOnly = false) => (
    <div className="po-files">
      {!readOnly ? (
        <div className="po-files__toolbar">
          <select
            value={uploadKind}
            onChange={(e) => setUploadKind(e.target.value as PurchaseOrderAttachmentKind)}
          >
            <option value="DRAWING">도면</option>
            <option value="SPEC">사양서</option>
            <option value="OTHER">기타</option>
          </select>
          <label className="is-upload">
            {uploading ? '업로드 중…' : immediateUpload ? '파일 업로드' : '파일 추가'}
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.dwg,.dxf,.doc,.docx,.hwp,.zip,.xlsx,.xls"
              multiple
              disabled={uploading || saving}
              onChange={(e) => {
                if (immediateUpload) {
                  void handleUploadNow(e.target.files);
                } else {
                  handlePickFiles(e.target.files);
                }
                e.target.value = '';
              }}
            />
          </label>
          <p className="po-files__hint">
            {immediateUpload
              ? '도면·사양서 등 선택 즉시 업로드됩니다.'
              : '저장 시 함께 업로드됩니다. 도면·PDF·이미지 권장.'}
          </p>
        </div>
      ) : null}

      {savedAttachments.length > 0 ? (
        <ul className="po-files__list">
          {savedAttachments.map((file) => (
            <li key={file.attachmentId} className="po-files__item">
              <div className="po-files__meta">
                <strong>{file.fileName}</strong>
                <span>
                  {purchaseOrderAttachmentKindLabel(file.attachmentKind)}
                  {file.fileSize != null ? ` · ${formatPurchaseOrderFileSize(file.fileSize)}` : ''}
                </span>
              </div>
              <div className="po-files__actions">
                <button
                  type="button"
                  className="contract-panel__ghost"
                  onClick={() => void handleDownloadAttachment(file)}
                >
                  다운로드
                </button>
                {!readOnly ? (
                  <button
                    type="button"
                    className="contract-panel__ghost is-danger"
                    onClick={() => void handleDeleteAttachment(file)}
                  >
                    삭제
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {!readOnly && pendingFiles.length > 0 ? (
        <ul className="po-files__list">
          {pendingFiles.map((item, index) => (
            <li key={`${item.file.name}-${index}`} className="po-files__item">
              <div className="po-files__meta">
                <strong>{item.file.name}</strong>
                <span>
                  {purchaseOrderAttachmentKindLabel(item.kind)} · 저장 시 업로드
                  {item.file.size ? ` · ${formatPurchaseOrderFileSize(item.file.size)}` : ''}
                </span>
              </div>
              <div className="po-files__actions">
                <button
                  type="button"
                  className="contract-panel__ghost"
                  onClick={() =>
                    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  제거
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {savedAttachments.length === 0 && (readOnly || pendingFiles.length === 0) ? (
        <p className="contract-panel__hint">첨부된 파일이 없습니다.</p>
      ) : null}
    </div>
  );

  const toolbarTitle =
    viewMode === 'create'
      ? '발주서 등록'
      : viewMode === 'edit'
        ? '발주서 수정'
        : viewMode === 'detail'
          ? '발주서 상세'
          : contractId != null
            ? '계약 발주서'
            : '발주서';

  const toolbarDesc =
    viewMode === 'list'
      ? contractId != null
        ? '이 계약에 연결된 발주만 표시합니다. 행을 클릭하면 상세로 이동합니다.'
        : '계약 없이도 발주할 수 있습니다. 행을 클릭하면 상세로 이동합니다.'
      : viewMode === 'detail'
        ? `${textOrDash(detail?.orderNo)} · ${purchaseOrderStatusLabel(
            detail?.status,
            detail?.approvalInProgress,
            purchaseOrderHasApprover(detail)
          )}`
        : '계약은 선택 사항입니다. 업체와 품목을 입력하세요.';

  const renderEditActions = () => (
    <div className="po-form__footer">
      <button type="button" className="contract-panel__ghost" onClick={backToList}>
        취소
      </button>
      <button type="submit" className="contract-panel__primary" disabled={saving}>
        {saving ? '저장 중…' : '저장 (작성완료)'}
      </button>
    </div>
  );

  const renderDetailActions = () => {
    if (!detail) return null;
    const approved = detail.status === 'APPROVED';
    const ordered = detail.status === 'ORDERED';
    const hasApprover = purchaseOrderHasApprover(detail);
    const canDownloadPdf = approved || ordered;
    const locked =
      approved ||
      ordered ||
      detail.status === 'CLOSED' ||
      detail.status === 'CANCELLED' ||
      !!detail.approvalInProgress;
    const canApproveWhileOrdered =
      ordered && !hasApprover && !detail.approvalInProgress;
    const canRequestApproval =
      (!locked && !detail.approvalInProgress) || canApproveWhileOrdered;
    const canShowApproveDirect =
      canApproveDirect &&
      ((!locked && !detail.approvalInProgress) || canApproveWhileOrdered);
    const canMarkOrdered =
      !detail.approvalInProgress &&
      (detail.status === 'DRAFT' || detail.status === 'APPROVED');
    return (
      <div className="ncr-actions" style={{ marginBottom: 12, justifyContent: 'flex-end' }}>
        {canDownloadPdf ? (
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={pdfDownloading}
            onClick={() => void handleDownloadPdf()}
          >
            {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
          </button>
        ) : null}
        {detail.canRevoke ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => void handleRevoke()}
          >
            {approving ? '철회 중…' : '승인 철회'}
          </button>
        ) : null}
        {detail.canCancelApproval ? (
          <ApprovalCancelButton
            detail={detail}
            className="ncr-btn is-secondary"
            disabled={approving}
            onCancelled={async () => {
              const next = await fetchPurchaseOrder(detail.purchaseOrderId);
              setDetail(next);
              setApprovalMessage('');
              void load();
            }}
          />
        ) : null}
        {!locked ? (
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={() => void openEdit(detail)}
          >
            수정
          </button>
        ) : null}
        {canRequestApproval ? (
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => setApprovalOpen(true)}
          >
            결재 요청
          </button>
        ) : null}
        {canShowApproveDirect ? (
          <button
            type="button"
            className="ncr-btn is-approve"
            disabled={approving}
            onClick={() => void handleApproveDirect()}
          >
            {approving ? '승인 중…' : '승인'}
          </button>
        ) : null}
        {canMarkOrdered ? (
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={approving}
            onClick={() => void handleMarkOrdered()}
          >
            {approving ? '처리 중…' : '발주 완료'}
          </button>
        ) : null}
        {detail.canCancelOrdered ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={approving}
            onClick={() => void handleCancelOrdered()}
          >
            {approving ? '취소 중…' : '발주 취소'}
          </button>
        ) : null}
        {!locked ? (
          <button
            type="button"
            className="ncr-btn is-danger"
            onClick={() => void handleDelete(detail)}
          >
            삭제
          </button>
        ) : null}
      </div>
    );
  };

  const renderForm = () => (
    <form className="po-form" onSubmit={(e) => void handleSubmit(e)}>
      {renderEditActions()}
      <section className="po-form__card">
        <h3>기본 정보</h3>
        <div className="po-form__grid">
          <label className="po-form__field">
            <span className="po-form__label">계약 (선택)</span>
            <select
              value={formContractId ?? ''}
              disabled={contractId != null}
              onChange={(e) => setFormContractId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">계약 없음</option>
              {contracts.map((c) => (
                <option key={c.contractId} value={c.contractId}>
                  {c.contractName}
                  {c.contractNo || c.documentNo
                    ? ` (${c.contractNo || c.documentNo})`
                    : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="po-form__field">
            발주번호
            <input
              value={viewMode === 'create' ? '' : orderNo}
              readOnly
              placeholder={viewMode === 'create' ? '저장 시 자동 발급' : undefined}
            />
            {viewMode === 'create' && orderNoHint ? (
              <span className="po-form__hint">{orderNoHint}</span>
            ) : null}
          </label>
          <label className="po-form__field">
            발주일
            <input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
          </label>
          <label className="po-form__field">
            납기일
            <input
              type="date"
              value={deliveryDueDate}
              onChange={(e) => setDeliveryDueDate(e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="po-form__card">
        <div className="po-form__card-head">
          <h3>업체</h3>
        </div>
        <div className="po-form__grid">
          <label className="po-form__field is-vendor-pick">
            등록 업체
            <div className="po-vendor-pick">
              <button
                type="button"
                className={`po-line__item-btn${vendorId != null || vendorName.trim() ? ' has-value' : ''}`}
                onClick={() => setVendorPickerOpen(true)}
              >
                {vendorName.trim()
                  ? vendorName.trim()
                  : vendors.length === 0
                    ? '업체 선택 (빠른 등록)'
                    : '업체 선택'}
              </button>
              {vendorId != null || vendorName.trim() ? (
                <button
                  type="button"
                  className="contract-panel__ghost po-vendor-pick__clear"
                  onClick={clearVendor}
                >
                  지우기
                </button>
              ) : null}
            </div>
          </label>
          <label className="po-form__field">
            업체명
            <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
          </label>
          <label className="po-form__field">
            담당자
            <input
              value={vendorContactName}
              onChange={(e) => setVendorContactName(e.target.value)}
            />
          </label>
          <label className="po-form__field">
            연락처
            <input value={vendorPhone} onChange={(e) => setVendorPhone(e.target.value)} />
          </label>
          <label className="po-form__field">
            Fax
            <input value={vendorFax} onChange={(e) => setVendorFax(e.target.value)} />
          </label>
        </div>
        {vendors.length === 0 ? (
          <p className="contract-panel__hint" style={{ marginTop: 8 }}>
            등록된 업체가 없습니다. 업체 선택 팝업의 「빠른 등록」으로 추가하세요.
          </p>
        ) : null}
      </section>

      <section className="po-form__card">
        <div className="po-form__card-head">
          <h3>발주 품목</h3>
          <div className="po-form__card-actions">
            <button
              type="button"
              className="contract-panel__ghost"
              onClick={() => setLines((prev) => [...prev, newLine()])}
            >
              행 추가
            </button>
          </div>
        </div>
        {inventory.length === 0 ? (
          <p className="contract-panel__hint" style={{ marginBottom: 10 }}>
            등록된 재고 품목이 없습니다. 품목 선택 팝업의 「빠른 등록」으로 추가하세요.
          </p>
        ) : null}
        <div className="po-lines">
          {lines.map((line, index) => {
            const lineAmount = calcOrderLineAmount(line.orderQtyText, line.unitPriceText);
            const summary = lineItemSummary(line);
            return (
              <div key={line.key} className="po-line">
                <span className="po-line__no">{index + 1}</span>
                <label className="po-form__field is-item">
                  품목
                  <button
                    type="button"
                    className={`po-line__item-btn${summary ? ' has-value' : ''}`}
                    onClick={() => setPickerLineKey(line.key)}
                  >
                    {summary || '품목 선택'}
                  </button>
                </label>
                <label className="po-form__field">
                  수량
                  <input
                    value={line.orderQtyText}
                    inputMode="decimal"
                    onChange={(e) =>
                      patchLine(line.key, { orderQtyText: formatUnitPriceInput(e.target.value) })
                    }
                  />
                </label>
                <label className="po-form__field">
                  단위
                  <input
                    value={line.unit}
                    onChange={(e) => patchLine(line.key, { unit: e.target.value })}
                  />
                </label>
                <label className="po-form__field">
                  단가
                  <input
                    value={line.unitPriceText}
                    inputMode="decimal"
                    onChange={(e) =>
                      patchLine(line.key, { unitPriceText: formatUnitPriceInput(e.target.value) })
                    }
                    placeholder="0"
                  />
                </label>
                <label className="po-form__field is-amount">
                  합계
                  <input
                    value={lineAmount == null ? '-' : lineAmount.toLocaleString('ko-KR')}
                    readOnly
                    tabIndex={-1}
                  />
                </label>
                <button
                  type="button"
                  className="contract-panel__ghost po-line__remove"
                  disabled={lines.length <= 1}
                  onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                >
                  삭제
                </button>
                <label className="po-form__field po-line__remark">
                  비고
                  <input
                    value={line.remark}
                    maxLength={500}
                    placeholder="선택 입력"
                    onChange={(e) => patchLine(line.key, { remark: e.target.value })}
                  />
                </label>
              </div>
            );
          })}
        </div>
        <div className="po-lines__total">
          <span>통합 합계</span>
          <strong>{formHasPricedLine ? formatWon(formTotalAmount) : '-'}</strong>
        </div>
      </section>

      <CompanyInventoryPickerModal
        open={pickerLineKey != null}
        items={inventory}
        masters={masters}
        onClose={() => setPickerLineKey(null)}
        onListsUpdated={(nextInventory, nextMasters) => {
          setInventory(nextInventory);
          setMasters(nextMasters);
        }}
        onSelect={(item) => {
          if (pickerLineKey == null) return;
          applyInventoryToLine(pickerLineKey, item);
        }}
      />

      <PartVendorPickerModal
        open={vendorPickerOpen}
        vendors={vendors}
        onClose={() => setVendorPickerOpen(false)}
        onListsUpdated={setVendors}
        onSelect={applyVendor}
      />

      <section className="po-form__card">
        <h3>첨부파일</h3>
        <p className="contract-panel__hint" style={{ marginBottom: 10 }}>
          도면·사양서 등 발주 관련 파일을 첨부할 수 있습니다.
        </p>
        {renderAttachmentEditor(viewMode === 'edit')}
      </section>

      <section className="po-form__card">
        <label className="po-form__field">
          메모
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={3} />
        </label>
      </section>

      {renderEditActions()}
    </form>
  );

  const renderDetail = () => {
    if (!detail) return null;
    const approved = detail.status === 'APPROVED';
    const ordered = detail.status === 'ORDERED';
    const hasApprover = purchaseOrderHasApprover(detail);
    const locked =
      approved ||
      ordered ||
      detail.status === 'CLOSED' ||
      detail.status === 'CANCELLED' ||
      !!detail.approvalInProgress;
    const statusText = purchaseOrderStatusLabel(
      detail.status,
      detail.approvalInProgress,
      hasApprover
    );
    return (
      <div className="po-detail ncr-panel">
        {renderDetailActions()}
        {approvalMessage ? (
          <p className="contract-panel__hint" style={{ color: '#1a5fb4' }}>
            {approvalMessage}
          </p>
        ) : null}
        {detail.approvalInProgress ? (
          <p className="contract-panel__hint">
            결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
          </p>
        ) : null}

        <div className="ifs-sheet" style={{ marginBottom: 12 }}>
          <div className="ifs-sheet__top">
            <div>
              <h3 style={{ margin: '0 0 4px' }}>발주서</h3>
              <p className="contract-panel__hint" style={{ margin: 0 }}>
                {textOrDash(detail.orderNo)} ·{' '}
                {statusText}
              </p>
            </div>
            <ApprovalStampBox
              authorName={detail.authorName}
              reviewerName={detail.reviewerName}
              approverName={detail.approverName}
            />
          </div>
        </div>

        <section className="po-form__card">
          <h3>기본 정보</h3>
          <dl className="po-detail__dl">
            <div>
              <dt>계약</dt>
              <dd>
                {textOrDash(detail.contractName)}
                {detail.contractNo ? ` (${detail.contractNo})` : ''}
              </dd>
            </div>
            <div>
              <dt>발주번호</dt>
              <dd>{textOrDash(detail.orderNo)}</dd>
            </div>
            <div>
              <dt>발주일</dt>
              <dd>{formatDate(detail.orderDate)}</dd>
            </div>
            <div>
              <dt>납기일</dt>
              <dd>{formatDate(detail.deliveryDueDate)}</dd>
            </div>
            <div>
              <dt>상태</dt>
              <dd>
                <span className={`po-status is-${(detail.status || 'DRAFT').toLowerCase()}`}>
                  {statusText}
                </span>
              </dd>
            </div>
          </dl>
        </section>
        <section className="po-form__card">
          <h3>업체</h3>
          <dl className="po-detail__dl">
            <div>
              <dt>업체명</dt>
              <dd>{textOrDash(detail.vendorName)}</dd>
            </div>
            <div>
              <dt>담당자</dt>
              <dd>{textOrDash(detail.vendorContactName)}</dd>
            </div>
            <div>
              <dt>연락처</dt>
              <dd>{textOrDash(detail.vendorPhone)}</dd>
            </div>
            <div>
              <dt>Fax</dt>
              <dd>{textOrDash(detail.vendorFax)}</dd>
            </div>
          </dl>
        </section>
        <section className="po-form__card">
          <h3>품목</h3>
          {!detail.items || detail.items.length === 0 ? (
            <p className="contract-panel__hint">품목이 없습니다.</p>
          ) : (
            <>
              <table className="po-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>품명</th>
                    <th>규격</th>
                    <th>수량</th>
                    <th>단위</th>
                    <th>단가</th>
                    <th>합계</th>
                    <th>비고</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((item, index) => {
                    const amount =
                      item.amount != null && item.amount !== ''
                        ? Number(item.amount)
                        : calcOrderLineAmount(item.orderQty, item.unitPrice);
                    return (
                      <tr key={item.itemId ?? index}>
                        <td>{index + 1}</td>
                        <td>{textOrDash(item.itemName)}</td>
                        <td>{textOrDash(item.specText)}</td>
                        <td>{formatOrderQty(item.orderQty)}</td>
                        <td>{textOrDash(item.unit)}</td>
                        <td>
                          {item.unitPrice == null || item.unitPrice === ''
                            ? '-'
                            : Number(item.unitPrice).toLocaleString('ko-KR')}
                        </td>
                        <td>{amount == null ? '-' : amount.toLocaleString('ko-KR')}</td>
                        <td>{textOrDash(item.remark)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="po-lines__total is-detail">
                <span>통합 합계</span>
                <strong>
                  {detail.totalAmount != null && detail.totalAmount !== ''
                    ? formatWon(detail.totalAmount)
                    : formatWon(
                        detail.items.reduce((sum, item) => {
                          const amount =
                            item.amount != null && item.amount !== ''
                              ? Number(item.amount)
                              : calcOrderLineAmount(item.orderQty, item.unitPrice);
                          return amount == null ? sum : sum + amount;
                        }, 0)
                      )}
                </strong>
              </div>
            </>
          )}
        </section>
        <section className="po-form__card">
          <h3>첨부파일</h3>
          {renderAttachmentEditor(
            true,
            locked || approved || Boolean(detail.approvalInProgress)
          )}
        </section>
        {detail.memo ? (
          <section className="po-form__card">
            <h3>메모</h3>
            <p className="po-detail__memo">{detail.memo}</p>
          </section>
        ) : null}
        {renderDetailActions()}
        {approvalOpen && detail ? (
          <ApprovalRequestModal
            open={approvalOpen}
            onClose={() => setApprovalOpen(false)}
            titleDefault={`${detail.orderNo?.trim() || `#${detail.purchaseOrderId}`} 발주서 결재`}
            targetType={APPROVAL_TARGET_PURCHASE_ORDER}
            targetId={detail.purchaseOrderId}
            onSubmitted={() => {
              setApprovalOpen(false);
              setApprovalMessage(
                '결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.'
              );
              void fetchPurchaseOrder(detail.purchaseOrderId).then(setDetail);
              void load();
            }}
          />
        ) : null}
      </div>
    );
  };

  const renderList = () =>
    loading ? (
      <p className="contract-panel__hint" style={{ padding: 16 }}>
        불러오는 중…
      </p>
    ) : filteredRows.length === 0 ? (
      <p className="contract-panel__hint" style={{ padding: 16 }}>
        등록된 발주서가 없습니다.
      </p>
    ) : (
      <table className="po-table is-clickable">
        <thead>
          <tr>
            <th>발주번호</th>
            {contractId == null ? <th>계약</th> : null}
            <th>업체</th>
            <th>발주일</th>
            <th>납기</th>
            <th>품목</th>
            <th>첨부</th>
            <th>합계</th>
            <th>상태</th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row) => (
            <tr key={row.purchaseOrderId} onClick={() => void openDetail(row)}>
              <td>{textOrDash(row.orderNo) === '-' ? `#${row.purchaseOrderId}` : row.orderNo}</td>
              {contractId == null ? <td>{textOrDash(row.contractName)}</td> : null}
              <td>{textOrDash(row.vendorName)}</td>
              <td>{formatDate(row.orderDate)}</td>
              <td>{formatDate(row.deliveryDueDate)}</td>
              <td>{row.itemCount ?? 0}</td>
              <td>{row.attachmentCount ?? 0}</td>
              <td>{formatWon(row.totalAmount)}</td>
              <td>
                <span className={`po-status is-${(row.status || 'DRAFT').toLowerCase()}`}>
                  {purchaseOrderStatusLabel(
                    row.status,
                    row.approvalInProgress,
                    purchaseOrderHasApprover(row)
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  return (
    <main className={`contract-panel po-panel-root${embedded ? ' is-embedded' : ''}`}>
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>발주서</strong>
          <span>{contractId != null ? '계약 연동' : '계약 → 발주 → 인수'}</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="발주번호·업체·계약 검색"
          />
        </div>
        <div className="contract-panel__list-wrap">
          <ul className="contract-panel__list">
            {STATUS_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  className={`contract-panel__list-item${
                    statusFilter === opt.value ? ' is-active' : ''
                  }`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  <span className="contract-panel__list-name">{opt.label}</span>
                  <span className="contract-panel__list-meta">
                    {statusCounts[opt.value] ?? 0}건
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="contract-panel__main po-panel__main">
        <div className="contract-panel__toolbar">
          <div>
            <h2>{toolbarTitle}</h2>
            <p>{toolbarDesc}</p>
          </div>
          {viewMode === 'list' ? (
            <button type="button" className="contract-panel__primary" onClick={openCreate}>
              발주 등록
            </button>
          ) : (
            <button type="button" className="contract-panel__ghost" onClick={backToList}>
              목록으로
            </button>
          )}
        </div>

        {error ? <p className="contract-panel__error">{error}</p> : null}

        <div className="po-panel__scroll">
          {viewMode === 'create' || viewMode === 'edit'
            ? renderForm()
            : viewMode === 'detail'
              ? renderDetail()
              : renderList()}
        </div>
      </section>
    </main>
  );
}

export default PurchaseOrderPanel;
